import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, PhoneCall, Send, CheckCircle2 } from 'lucide-react';
import { websiteData } from '../data/websiteData.js';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'Pre-Engineered Buildings',
    details: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate submission block
    console.log("Form Submitted:", formData);
    setIsSubmitted(true);
    // Reset form fields
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: 'Pre-Engineered Buildings',
      details: ''
    });
  };

  const contactItems = [
    {
      title: "Call Us",
      value: websiteData.contact.phoneDisplay,
      link: `tel:${websiteData.contact.phone}`,
      icon: Phone
    },
    {
      title: "Email Us",
      value: websiteData.contact.email,
      link: `mailto:${websiteData.contact.email}`,
      icon: Mail
    },
    {
      title: "Our Location",
      value: websiteData.contact.address,
      link: websiteData.contact.googleMapsLink,
      icon: MapPin
    },
    {
      title: "Business Hours",
      value: websiteData.contact.hours,
      link: null,
      icon: Clock
    }
  ];

  return (
    <section id="contact" className="section contact">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <h2>Let's Build Something Strong</h2>
          <p>
            Have a project requirement? Contact Vijay Industry to discuss your structural, roofing, or fabrication needs.
          </p>
        </div>

        {/* Contact Info and Form Grid */}
        <div className="contact-split">
          {/* Left Column: Contact Cards + Call Now CTA */}
          <div className="contact-info-panel">
            {contactItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="contact-info-item">
                  <div className="contact-info-icon" aria-hidden="true">
                    <IconComponent size={20} />
                  </div>
                  <div className="contact-info-text">
                    <h3>{item.title}</h3>
                    {item.link ? (
                      <a href={item.link} className="contact-link" style={{ color: 'var(--secondary)', fontWeight: 600 }}>
                        {item.value}
                      </a>
                    ) : (
                      <p>{item.value}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Accessible Phone CTA (Crucial for Mobile) */}
            <div className="contact-mobile-cta">
              <div className="contact-mobile-header">
                <PhoneCall size={20} style={{ color: 'var(--accent)' }} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#FFFFFF' }}>Instant Direct Call</h3>
              </div>
              <p style={{ color: '#E2E8F0', fontSize: '0.85rem' }}>
                Need an immediate consultation or urgent site measurement? Tap below to call our engineer directly.
              </p>
              <a 
                href={`tel:${websiteData.contact.phone}`} 
                className="btn btn-primary btn-block"
                style={{ fontSize: '1.1rem', fontWeight: '700' }}
              >
                Call Now: {websiteData.contact.phoneDisplay}
              </a>
            </div>
          </div>

          {/* Right Column: Quote Request Form */}
          <div className="contact-form-panel">
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--primary)' }}>Request a Quote</h3>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  className="form-control" 
                  placeholder="Enter your name" 
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  className="form-control" 
                  placeholder="Enter your mobile number" 
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  className="form-control" 
                  placeholder="Enter your email (optional)" 
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="service">Service Required *</label>
                <select 
                  id="service" 
                  name="service" 
                  className="form-control" 
                  value={formData.service}
                  onChange={handleChange}
                  required
                >
                  <option value="Pre-Engineered Buildings">Pre-Engineered Buildings</option>
                  <option value="Factory Sheds">Factory Sheds</option>
                  <option value="Warehouses">Warehouses</option>
                  <option value="Showrooms">Showrooms</option>
                  <option value="Mezzanine Floors">Mezzanine Floors</option>
                  <option value="Durable & Cost Effective Solutions">Durable & Cost Effective Solutions</option>
                  <option value="Other Structural Needs">Other Structural Needs</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="details">Project Details *</label>
                <textarea 
                  id="details" 
                  name="details" 
                  className="form-control" 
                  placeholder="Please describe your requirements (e.g. dimensions, project location, material specifications)" 
                  value={formData.details}
                  onChange={handleChange}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" style={{ marginTop: '0.5rem' }}>
                <Send size={16} /> Request a Quote
              </button>
            </form>

            {isSubmitted && (
              <div className="form-success-alert animate-fade-in" role="alert">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
                  <CheckCircle2 size={20} />
                  <span>Submission Successful!</span>
                </div>
                <p style={{ color: '#155724', fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Thank you. We have received your structural request. An engineer will contact you shortly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
